import React from "react";

import defaultStyles from "./main.module.scss";

type ChangeEvent = React.ChangeEvent<HTMLTextAreaElement>;
type EventArgs = {
  name: ChangeEvent["target"]["name"];
  value: ChangeEvent["target"]["value"];
  _e: ChangeEvent;
};

const defaultValidate = (e: EventArgs) => e;

export interface TextInputProps
  extends Omit<React.ComponentPropsWithoutRef<"textarea">, "onChange"> {
  validate?: (options: EventArgs) => EventArgs;
  onChange: (options: EventArgs) => EventArgs | void;
  /**
   * Make sure to pass only Label instance from ui/src/components/form/Label
   */
  label?: React.ReactElement<any>;
  /**
   * These classes are appended to the parent container
   */
  classes?: string;
  /**
   * If the form field has error, pass this as true
   */
  error?: boolean;
  styles?: ModularStyles;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  inputContainerClass?: string;
}

const TextareaInput = React.forwardRef<HTMLTextAreaElement, TextInputProps>(
  (
    {
      onChange = null,
      validate = defaultValidate,
      autoComplete = "on",
      label = null,
      id = `input-${Date.now()}`,
      autoFocus = false,
      name = "textarea-input",
      value = "",
      disabled = false,
      error = false,
      styles = defaultStyles,
      className = "",
      classes = "",
      inputContainerClass = "",
      prefix,
      suffix,
      ...restProps
    },
    ref,
  ) => {
    return (
      <div className={`${styles.formInput} ${error ? styles.formInputError : ""} ${classes}`}>
        {label && React.cloneElement(label, { disabled, htmlFor: id, "aria-disabled": disabled })}
        <span className={inputContainerClass}>
          {prefix}
          <textarea
            className={`${styles.inputClass} ${className}`}
            name={name}
            id={id}
            ref={ref}
            disabled={disabled}
            autoFocus={autoFocus}
            autoComplete={autoComplete}
            value={value || ""}
            onChange={(e) =>
              onChange(validate({ name: e.target.name, value: e.target.value, _e: e }))
            }
            {...restProps}
          />
          {suffix}
        </span>
      </div>
    );
  },
);

export default TextareaInput;
